/**
 * NextTopicsPanel — Coverage Phase B Sprint 6 Prompt 6.5 (web).
 *
 * Replaces the legacy "Priority topics" card (which sorted periods by lowest
 * %, useless when most periods read 0%) with the actionable next-3 cards
 * server-side derivation. See apps/api/src/services/coverage-v2.getNextTopics.
 *
 * Each row is a one-line invitation + a "Talk about this →" CTA that opens a
 * new session pre-pointed at the card via `cardIdHint`. The session start
 * route accepts `cardId` (UUID) — but next-topics returns the human-readable
 * `cardId` text, so the CTA navigates to the deck-aware "/interviews/start?
 * cardId=..." path that the existing planner Rule 0 already honors.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { fetchNextTopics, nextTopicsQueryKey, type NextTopic } from '@/services/coverage-v2';
import { cn } from '@/lib/utils';

interface NextTopicsPanelProps {
  className?: string;
  /** Forward to the deep-link so admin "test as user" sessions stay scoped. */
  targetUserId?: string | null;
  /** Number of topics to show. Defaults to 3 per the design spec. */
  k?: number;
  /**
   * Called when the user clicks "Talk about this →". Optional — when
   * provided, the parent can short-circuit to its own session-start flow
   * instead of route navigation. Falls through to a Link otherwise.
   */
  onPickTopic?: (topic: NextTopic) => void;
}

export function NextTopicsPanel({
  className,
  targetUserId,
  k = 3,
  onPickTopic,
}: NextTopicsPanelProps) {
  const { data, isLoading, error } = useQuery<NextTopic[]>({
    queryKey: nextTopicsQueryKey(targetUserId),
    queryFn: () => fetchNextTopics(k),
    staleTime: 30_000,
    retry: 1,
  });

  return (
    <div className={cn('rounded-lg border bg-card p-5', className)}>
      <h3 className="font-display text-base font-semibold text-foreground">Next topics</h3>
      <p className="mt-1 text-xs text-muted-foreground">We&rsquo;ll come back to these soon.</p>

      {isLoading && (
        <div className="mt-3 flex items-center justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <p className="mt-3 text-xs text-destructive">Unable to load suggestions right now.</p>
      )}

      {!isLoading && !error && (data?.length ?? 0) === 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          You&rsquo;re ahead of us — no fresh topics queued.
        </p>
      )}

      {!isLoading && !error && (data?.length ?? 0) > 0 && (
        <ul className="mt-3 space-y-2 text-sm" aria-label="Next topics">
          {data!.map((topic) => (
            <li
              key={topic.cardId}
              className="flex items-start justify-between gap-3 border-b border-border/40 pb-2 last:border-b-0 last:pb-0"
            >
              <span className="flex-1 font-display text-foreground">{topic.hook}</span>
              {onPickTopic ? (
                <button
                  type="button"
                  onClick={() => onPickTopic(topic)}
                  className="flex shrink-0 items-center gap-1 text-[11px] text-orb-teal hover:underline"
                >
                  Talk about this
                  <ArrowRight className="h-3 w-3" />
                </button>
              ) : (
                <Link
                  to={buildStartHref(topic.cardId, targetUserId)}
                  className="flex shrink-0 items-center gap-1 text-[11px] text-orb-teal hover:underline"
                >
                  Talk about this
                  <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Build a deep-link to the interview screen pre-pointed at a card. The
 * Interview page reads `cardIdHint` from the URL on mount (existing behavior
 * — see useInterviewSession). We pass it as a query param so the planner's
 * Rule 0 can honor the hint.
 */
function buildStartHref(cardId: string, targetUserId?: string | null): string {
  const params = new URLSearchParams();
  params.set('cardIdHint', cardId);
  if (targetUserId) params.set('asUser', targetUserId);
  return `/dashboard/ai-playground/interview?${params.toString()}`;
}
